import { Component, Input, ElementRef } from '@angular/core';

import * as _ from 'lodash';

import { RippleDirective } from './directives/ripple.directive';

/**
 * Define estilos de botÃµes para elementos. Por razÃµes de acessibilidade, os botÃµes usam um elemento button padrÃ£o.
 *
 * Ã uma abstraÃ§Ã£o para os [botÃµes do Bootstrap 4]{@link https://getbootstrap.com/docs/4.1/components/buttons}, com algumas melhorias e estilos personalizados.
 *
 * Demo: {@link https://gituex.gitlab.io/extensions/web-extensions#components/button}
 */
@Component({
  selector: '[uex-button]',
  templateUrl: './button.component.html',
  host: {
    'class': 'uex-button',
    '[attr.type]': 'is_button ? type : null',
    '[class.with-ripple]': 'hasRipple()',
    '[class.ripple-dark]': 'isDarkRipple()'
  }
})
export class ButtonComponent extends RippleDirective {

  /**
   * Define o atributo "title" de layout que serÃ¡ aplicado ao botÃ£o
   */
  @Input('type') type = 'button';

  /**
   * Define o modo de layout que serÃ¡ aplicado ao botÃ£o
   */
  @Input('mode')
  private set _setMode(mode: ButtonMode) {
    this._mode = mode;
    this._updateClasses();
  }

  /**
   * Define a cor que serÃ¡ aplicada ao botÃ£o
   */
  @Input('color')
  private set _setColor(color: string) {
    this._color = color;
    this._updateClasses();
  }

  /**
   * Define o tamanho que serÃ¡ aplicado ao botÃ£o
   */
  @Input('size')
  private set _setSize(size: ButtonSize) {
    this._size = size;
    this._updateClasses();
  }

  /**
   * Define se serÃ¡ aplicada borda arredondada ao botÃ£o
   */
  @Input('rounded')
  private set _setRounded(rounded: boolean) {
    this._rounded = this._getAttribute(rounded, true);
    this._updateClasses();
  }

  /**
   * Define se serÃ¡ aplicada largura "100%" arredondada ao botÃ£o
   */
  @Input('block')
  private set _setBlock(block: boolean) {
    this._block = this._getAttribute(block, true);
    this._updateClasses();
  }

  /**
   * Define se o botÃ£o deverÃ¡ se ajustar para receber apenas um icone
   */
  @Input('icon-only')
  private set _setIconOnly(icon_only: boolean) {
    this._icon_only = this._getAttribute(icon_only, true);
    this._updateClasses();
  }

  /**
   * Define se o botÃ£o deverÃ¡ se ajustar para receber um icone a direita
   */
  @Input('icon-right')
  private set _setIconRight(icon_right: boolean) {
    this._icon_right = this._getAttribute(icon_right, true);
    this._updateClasses();
  }

  /**
   * Define se o botÃ£o deverÃ¡ se ajustar para receber um icone a esquerda
   */
  @Input('icon-left')
  private set _setIconLeft(icon_left: boolean) {
    this._icon_left = this._getAttribute(icon_left, true);
    this._updateClasses();
  }

  /**
   * @ignore
   */
  constructor(
    public elementRef: ElementRef
  ) {
    super(elementRef);
  }

  private _color = 'primary';
  private _mode: ButtonMode = 'raised';
  private _size: ButtonSize = 'default';
  private _rounded = false;
  private _block = false;
  private _icon_only = false;
  private _icon_right = false;
  private _icon_left = false;

  private _classes: Array<string> = [];

  public is_button = false;

  public ngOnInit() {
    this.is_button = this.elementRef.nativeElement.tagName == 'BUTTON';
    this._updateClasses();
  }

  /**
   * DistÃ¢ncia do elemento para o topo do elemento pai mais prÃ³ximo posicionado com relative
   */
  public get offsetTop(): number {
    return this.elementRef.nativeElement.offsetTop;
  }

  /**
   * Dispara o gatilho de focus do botÃ£o
   *
   * @param [has_delay=false] - Adiciona um delay de 300ms para disparar o gatilho depois
   */
  public focus(has_delay: boolean = false) {
    if (has_delay) {
      setTimeout(() => this._doFocus(), 300);
    } else {
      this._doFocus();
    }
  }

  /**
   * @internal
   */
  public hasRipple(): boolean {
    return !this.no_ripple && !this._isOutline();
  }

  /**
   * @internal
   */
  public isDarkRipple(): boolean {
    return this.hasRipple() && this._isClear();
  }


  // Modes

  private _isFab(): boolean {
    return this._mode == 'fab';
  }

  private _isFabFlat(): boolean {
    return this._mode == 'fab-flat';
  }

  private _isClear(): boolean {
    return this._mode == 'clear';
  }

  private _isIcon(): boolean {
    return this._mode == 'icon';
  }

  private _isOutline(): boolean {
    return this._mode == 'outline';
  }

  private _isRaised(): boolean {
    return this._mode == 'raised';
  }

  // Sizes

  private _isXSmall(): boolean {
    return this._size == 'micro' || this._size == 'xsmall' || this._size == 'xs';
  }

  private _isSmall(): boolean {
    return this._size == 'mini' || this._size == 'small' || this._size == 'sm';
  }

  private _isLarge(): boolean {
    return this._size == 'large' || this._size == 'lg';
  }

  private _isXLarge(): boolean {
    return this._size == 'xlarge' || this._size == 'xl';
  }

  // Miscellaneous

  private _isRounded(): boolean {
    return this._rounded && !this._isIcon() && !this._isFab() && !this._isFabFlat();
  }

  private _isBlock(): boolean {
    return this._block && !this._isIcon() && !this._isFab() && !this._isFabFlat();
  }

  private _isIconOnly(): boolean {
    return this._icon_only && !this._isIcon() && !this._isFab() && !this._isFabFlat();
  }

  private _isIconRight(): boolean {
    return this._icon_right && !this._isIcon() && !this._isFab() && !this._isFabFlat();
  }

  private _isIconLeft(): boolean {
    return this._icon_left && !this._isIcon() && !this._isFab() && !this._isFabFlat();
  }

  // Colors

  private _updateClasses() {
    this._clearClasses();

    this._classes.push('btn');

    if (this._isFab()) { this._classes.push('btn-fab'); }
    if (this._isFabFlat()) { this._classes.push('btn-fab-flat'); }
    if (this._isClear()) { this._classes.push('btn-clear'); }
    if (this._isIcon()) { this._classes.push('btn-icon'); }
    if (this._isOutline()) { this._classes.push('btn-outline'); }
    if (this._isRaised()) { this._classes.push('btn-raised'); }

    if (this._isRounded()) { this._classes.push('btn-rounded'); }
    if (this._isBlock()) { this._classes.push('btn-block'); }
    if (this._isIconOnly()) { this._classes.push('btn-icon-only'); }
    if (this._isIconRight()) { this._classes.push('btn-icon-right'); }
    if (this._isIconLeft()) { this._classes.push('btn-icon-left'); }

    if (this._isXSmall()) { this._classes.push('btn-xs'); }
    if (this._isSmall()) { this._classes.push('btn-sm'); }
    if (this._isLarge()) { this._classes.push('btn-lg'); }
    if (this._isXLarge()) { this._classes.push('btn-xl'); }

    if (this._color == 'link') {
      this._classes.push(`btn-${this._color}`);
    } else {
      this._classes.push(`btn-${this._isOutline() ? 'outline-' : ''}${this._color || 'default'}`);
    }

    this._setClasses();
  }

  private _clearClasses() {
    if (this._classes) {
      const current_classes = this.elementRef.nativeElement.className.split(' ');
      this._classes.forEach((_class) => {
        const index = current_classes.indexOf(_class);
        if (index > -1) { current_classes.splice(index, 1); }
      });
      this._classes = [];
      this.elementRef.nativeElement.className = current_classes.join(' ');
    }
  }

  private _setClasses() {
    const current_classes = this.elementRef.nativeElement.className.split(' ').filter((i) => !!i);
    this.elementRef.nativeElement.className = _.concat(current_classes, this._classes).join(' ');
  }

  private _doFocus() {
    this.elementRef.nativeElement.focus();
  }

  private _getAttribute<T>(value: any, boolean: boolean = false): any {
    if (boolean) { return (_.isString(value) && value !== 'false') || (_.isBoolean(value) && !!value); }
    return !!value ? value : null;
  }
}

type ButtonModeDefault = 'flat';
type ButtonModeFab = 'fab' | 'fab-flat';
type ButtonModeClear = 'clear';
type ButtonModeIcon = 'icon';
type ButtonModeRaised = 'raised';
type ButtonModeOutline = 'outline';

/**
 * Modos suportados pelo botÃ£o
 */
export type ButtonMode = ButtonModeDefault | ButtonModeFab | ButtonModeClear | ButtonModeIcon | ButtonModeRaised | ButtonModeOutline;

type ButtonSizeDefault = 'default';
type ButtonSizeXSmall = 'micro' | 'xsmall' | 'xs';
type ButtonSizeSmall = 'mini' | 'small' | 'sm';
type ButtonSizeLarge = 'large' | 'lg';
type ButtonSizeXLarge = 'xlarge' | 'xl';

/**
 * Tamanhos suportados pelo botÃ£o
 */
export type ButtonSize = ButtonSizeDefault | ButtonSizeXSmall | ButtonSizeSmall | ButtonSizeLarge | ButtonSizeXLarge;
